import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, ArrowRightIcon } from 'lucide-react';
export function FundingComparison() {
  const [activeTab, setActiveTab] = useState('mca');
  const options = {
    mca: {
      title: 'Merchant Cash Advance',
      range: '$5K - $500K',
      timeline: '24-48 Hours',
      bestFor: 'Businesses with high credit card sales needing fast cash',
      credit: 'Easy (500+)',
      rates: 'Factor rate 1.1 - 1.5',
      pros: ['Extremely fast funding', 'No collateral required', 'High approval rates'],
      cons: ['Higher cost of capital', 'Daily/weekly repayments', 'Short repayment terms']
    },
    sba: {
      title: 'SBA Loans',
      range: '$50K - $5M',
      timeline: '4-12 Weeks',
      bestFor: 'Business expansion, real estate, refinancing',
      credit: 'Strict (680+)',
      rates: 'Prime + 2.25% to 4.75%',
      pros: ['Lowest interest rates', 'Long repayment terms (10-25 years)', 'Government backed'],
      cons: ['Lengthy application process', 'Strict eligibility requirements', 'Collateral often required']
    },
    loc: {
      title: 'Business Line of Credit',
      range: '$10K - $500K',
      timeline: '1-2 Weeks',
      bestFor: 'Managing cash flow, short-term expenses',
      credit: 'Moderate (600+)',
      rates: '8% - 24%',
      pros: ['Pay interest only on what you use', 'Revolving funds available anytime', 'Flexible repayment'],
      cons: ['Variable interest rates', 'Annual fees may apply', 'Lower limits than term loans']
    },
    equipment: {
      title: 'Equipment Financing',
      range: '$5K - $5M',
      timeline: '2-5 Days',
      bestFor: 'Purchasing machinery, vehicles, technology',
      credit: 'Easy (550+)',
      rates: '6% - 16%',
      pros: ['Equipment serves as collateral', 'Potential tax benefits (Section 179)', 'Preserves working capital'],
      cons: ['Can only be used for equipment', 'Depreciation of assets', 'Down payment may be required']
    }
  };
  return <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-black" data-id="element-255">
      <div className="max-w-7xl mx-auto" data-id="element-256">
        <div className="text-center mb-12" data-id="element-257">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-258">
            Compare Funding Options
          </h2>
          <p className="text-xl text-zinc-400" data-id="element-259">
            Find the right capital solution for your business goals
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-260">
          {Object.entries(options).map(([key, option]) => <button key={key} onClick={() => setActiveTab(key)} className={`px-6 py-3 rounded-full font-medium transition-all text-sm md:text-base ${activeTab === key ? 'bg-white text-black shadow-lg scale-105' : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 border border-zinc-800'}`} data-id="element-261">
              {option.title}
            </button>)}
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto" data-id="element-262">
          <AnimatePresence mode="wait" data-id="element-263">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="bg-zinc-900/50 rounded-2xl shadow-2xl overflow-hidden border border-zinc-800 backdrop-blur-md" data-id="element-264">
              <div className="p-8 md:p-10" data-id="element-265">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b border-zinc-800 pb-6" data-id="element-266">
                  <div data-id="element-267">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-268">
                      {options[activeTab as keyof typeof options].title}
                    </h3>
                    <p className="text-zinc-400" data-id="element-269">
                      {options[activeTab as keyof typeof options].bestFor}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0 flex flex-col items-end" data-id="element-270">
                    <span className="text-sm text-zinc-500 uppercase tracking-wide font-semibold" data-id="element-271">
                      Funding Range
                    </span>
                    <span className="text-2xl font-bold text-white" data-id="element-272">
                      {options[activeTab as keyof typeof options].range}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="element-273">
                  <div className="space-y-4" data-id="element-274">
                    <div className="flex justify-between p-3 bg-black/40 rounded-lg border border-zinc-800" data-id="element-275">
                      <span className="font-medium text-zinc-400" data-id="element-276">
                        Typical Timeline
                      </span>
                      <span className="font-bold text-white" data-id="element-277">
                        {options[activeTab as keyof typeof options].timeline}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-black/40 rounded-lg border border-zinc-800" data-id="element-278">
                      <span className="font-medium text-zinc-400" data-id="element-279">
                        Credit Requirement
                      </span>
                      <span className={`font-bold px-2 py-0.5 rounded text-sm ${options[activeTab as keyof typeof options].credit.includes('Strict') ? 'bg-red-900/30 text-red-200' : options[activeTab as keyof typeof options].credit.includes('Moderate') ? 'bg-yellow-900/30 text-yellow-200' : 'bg-green-900/30 text-green-200'}`} data-id="element-280">
                        {options[activeTab as keyof typeof options].credit}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-black/40 rounded-lg border border-zinc-800" data-id="element-281">
                      <span className="font-medium text-zinc-400" data-id="element-282">
                        Rates & Terms
                      </span>
                      <span className="font-bold text-white" data-id="element-283">
                        {options[activeTab as keyof typeof options].rates}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-284">
                    <div data-id="element-285">
                      <h4 className="font-bold text-white mb-2 flex items-center gap-2" data-id="element-286">
                        <CheckIcon className="w-4 h-4 text-green-400" data-id="element-287" /> Pros
                      </h4>
                      <ul className="space-y-2" data-id="element-288">
                        {options[activeTab as keyof typeof options].pros.map((pro, i) => <li key={i} className="text-sm text-zinc-400 flex items-start gap-2" data-id="element-289">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" data-id="element-290" />
                              {pro}
                            </li>)}
                      </ul>
                    </div>
                    <div data-id="element-291">
                      <h4 className="font-bold text-white mb-2 flex items-center gap-2" data-id="element-292">
                        <XIcon className="w-4 h-4 text-red-400" data-id="element-293" /> Cons
                      </h4>
                      <ul className="space-y-2" data-id="element-294">
                        {options[activeTab as keyof typeof options].cons.map((con, i) => <li key={i} className="text-sm text-zinc-400 flex items-start gap-2" data-id="element-295">
                              <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 shrink-0" data-id="element-296" />
                              {con}
                            </li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="text-center" data-id="element-297">
                  <a href="/application" className="w-full md:w-auto bg-white text-black px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-colors shadow-lg inline-flex items-center justify-center gap-2 group" data-id="element-298">
                    Apply for {options[activeTab as keyof typeof options].title}
                    <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-299" />
                  </a>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}